import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import SlideElement from './SlideElement';

import type { ResolvedTheme, Slide } from '@/shared/types/slide';
import { SLIDE_PAGE_WIDTH, SLIDE_PAGE_HEIGHT } from '@/shared/types/slide';
import { buildRenderElements, resolveSlideBackground } from '@/shared/theming/resolve';
import { resolveSignedUrl, type StorageBucket } from '@/shared/services/storageService';

// ---------------------------------------------------------------------------
// PresentationOverlay
//
// Fullscreen presentation mode for slides.  Renders as a portal to
// document.body, enters browser fullscreen on mount, and provides
// keyboard / click navigation between slides.
// ---------------------------------------------------------------------------

const INACTIVITY_MS = 3_000;
const noop = () => {};

async function preloadSlideImages(slide: Slide | undefined, theme: ResolvedTheme) {
  if (!slide) return;
  const elements = buildRenderElements(slide, theme);
  await Promise.all(
    elements
      .filter((el) => el.type === 'image')
      .map(async (el) => {
        const content = el.content as {
          src?: string;
          storageBucket?: string;
          storagePath?: string;
        };
        try {
          const src =
            content.storagePath && content.storageBucket
              ? await resolveSignedUrl(content.storageBucket as StorageBucket, content.storagePath)
              : content.src;
          if (!src) return;
          const img = new Image();
          img.src = src;
        } catch {
          // Best-effort preload; SlideElement will surface the real error on render.
        }
      })
  );
}

interface PresentationOverlayProps {
  slides: Slide[];
  theme: ResolvedTheme;
  startSlideIndex?: number;
  onExit: () => void;
}

const PresentationOverlay: React.FC<PresentationOverlayProps> = ({
  slides,
  theme,
  startSlideIndex = 0,
  onExit,
}) => {
  const [currentIndex, setCurrentIndex] = useState(
    Math.max(0, Math.min(startSlideIndex, slides.length - 1))
  );
  const [scale, setScale] = useState(1);
  const [idle, setIdle] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const exitCalledRef = useRef(false);

  const currentSlide = slides[currentIndex] ?? null;
  const total = slides.length;

  // Re-focus the wrapper after every slide change so keyboard nav keeps working
  // (focus can be lost when an interactive widget's button is removed on slide advance)
  useEffect(() => {
    wrapperRef.current?.focus();
  }, [currentIndex]);

  useEffect(() => {
    void preloadSlideImages(slides[currentIndex], theme);
    void preloadSlideImages(slides[currentIndex + 1], theme);
    void preloadSlideImages(slides[currentIndex - 1], theme);
  }, [slides, theme, currentIndex]);

  // -- helpers --------------------------------------------------------------

  const safeExit = useCallback(() => {
    if (exitCalledRef.current) return;
    exitCalledRef.current = true;
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
    onExit();
  }, [onExit]);

  const resetIdleTimer = useCallback(() => {
    setIdle(false);
    clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => setIdle(true), INACTIVITY_MS);
  }, []);

  // -- compute scale to fit viewport ---------------------------------------

  const computeScale = useCallback(() => {
    setScale(
      Math.min(window.innerWidth / SLIDE_PAGE_WIDTH, window.innerHeight / SLIDE_PAGE_HEIGHT)
    );
  }, []);

  // -- mount: request fullscreen + compute scale ----------------------------

  useEffect(() => {
    computeScale();
    resetIdleTimer();

    const el = document.documentElement as HTMLElement & {
      webkitRequestFullscreen?: () => Promise<void>;
    };
    const request = el.requestFullscreen?.bind(el) ?? el.webkitRequestFullscreen?.bind(el);
    request?.()?.catch(() => {
      // Fullscreen may be blocked by browser policy — still show the overlay
    });

    // Focus the wrapper so keyboard events are captured immediately
    wrapperRef.current?.focus();

    return () => clearTimeout(idleTimerRef.current);
  }, [computeScale, resetIdleTimer]);

  // -- listen for native fullscreen exit (e.g. browser Escape) --------------

  useEffect(() => {
    const onFullscreenChange = () => {
      if (!document.fullscreenElement) {
        safeExit();
      }
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
    };
  }, [safeExit]);

  // -- resize handler -------------------------------------------------------

  useEffect(() => {
    const onResize = () => computeScale();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [computeScale]);

  // -- keyboard navigation --------------------------------------------------

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Always reset idle on any key
      e.stopPropagation();
      resetIdleTimer();

      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          safeExit();
          return;
        case 'ArrowRight':
        case 'ArrowDown':
        case ' ':
          e.preventDefault();
          setCurrentIndex((i) => Math.min(i + 1, total - 1));
          return;
        case 'ArrowLeft':
        case 'ArrowUp':
        case 'Backspace':
          e.preventDefault();
          setCurrentIndex((i) => Math.max(i - 1, 0));
          return;
        case 'Home':
          e.preventDefault();
          setCurrentIndex(0);
          return;
        case 'End':
          e.preventDefault();
          setCurrentIndex(total - 1);
          return;
      }
    },
    [total, safeExit, resetIdleTimer]
  );

  // -- click to advance -----------------------------------------------------

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      // Don't advance if user clicked on an interactive widget
      const target = e.target as HTMLElement;
      if (target.closest('[data-interactive-widget]')) return;

      resetIdleTimer();
      setCurrentIndex((i) => Math.min(i + 1, total - 1));
    },
    [total, resetIdleTimer]
  );

  // -- mouse move: reset idle timer -----------------------------------------

  const handleMouseMove = useCallback(() => {
    resetIdleTimer();
  }, [resetIdleTimer]);

  // -- set inert on app root while mounted ----------------------------------

  useEffect(() => {
    const appRoot = document.getElementById('root');
    if (appRoot) appRoot.setAttribute('inert', '');
    return () => {
      if (appRoot) appRoot.removeAttribute('inert');
    };
  }, []);

  // -- render ---------------------------------------------------------------

  if (!currentSlide) {
    safeExit();
    return null;
  }

  const overlay = (
    <div
      ref={wrapperRef}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onClick={handleClick}
      onMouseMove={handleMouseMove}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: '#000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        outline: 'none',
        cursor: idle ? 'none' : 'default',
      }}
    >
      {/* Scaled slide container */}
      <div
        style={{
          width: SLIDE_PAGE_WIDTH,
          height: SLIDE_PAGE_HEIGHT,
          transform: `scale(${scale})`,
          transformOrigin: 'center center',
          backgroundColor: resolveSlideBackground(currentSlide, theme),
          position: 'relative',
          flexShrink: 0,
          overflow: 'hidden',
        }}
      >
        {buildRenderElements(currentSlide, theme).map((el) => (
          <SlideElement
            key={el.id}
            element={el}
            isSelected={false}
            zoom={100}
            canEdit={false}
            isPresenting
            onSelect={noop}
          />
        ))}
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
};

export default PresentationOverlay;
